import React, { useEffect, useState } from "react";
import { apiService } from "../../api/apiService";
import { useAuth } from "../../hooks/useAuth";
import {
    ZDARZENIE_OCHRONY_TRESCI,
    czyStosowacBlokady,
    uruchomOchroneTresci,
} from "../../utils/ochronaTresci";
import ZnakWodny from "./ZnakWodny";

/** Najkrótszy odstęp między sprawdzeniami wywołanymi powrotem do karty. Zdarzenie z panelu sprawdza ZAWSZE. */
export const ODSTEP_SPRAWDZEN_MS = 60 * 1000;

/** Podpis stanu — po nim poznajemy, że odpowiedź serwera NIC nie zmienia i nie ma po co przekładać nasłuchów. */
export const podpisUstawien = (u) => JSON.stringify([
    !!u?.blokadaF12, !!u?.blokadaZrodla, !!u?.blokadaKopiowania,
    !!u?.blokadaMenuKontekstowego, !!u?.znakWodny, u?.blokadyStosowane !== false, u?.podpis || "",
]);

/**
 * OCHRONA TREŚCI — jeden punkt, w którym aplikacja pyta serwer, które suwaki z /admin/integrations
 * („Monitoring aktywności") obowiązują TĘ osobę, i nakłada odpowiadające im zachowania.
 *
 * [polecenie właściciela 2026-09-19] „zmiany mają mieć charakter globalny — każdy taki przycisk oddzielny suwak".
 *
 * Ustawienie jest GLOBALNE i trzymane po stronie serwera (AppSettings), więc przeżywa przeładowanie strony,
 * zmianę przeglądarki i wylogowanie — komponent pyta o nie przy każdym wejściu. Zmiana działa BEZ PRZEŁADOWANIA:
 *  • w karcie administratora od razu (panel wysyła `ZDARZENIE_OCHRONY_TRESCI` po udanym zapisie),
 *  • u pozostałych po powrocie do karty (`focus` / `visibilitychange`), nie częściej niż raz na minutę.
 * Świadomie BEZ zegara odpytującego w tle: taki zegar to jedno żądanie na minutę razy liczba zalogowanych osób,
 * a maska nagrania ma już własny (audyt wydajności 2026-09-12 — koszt tej aplikacji to LICZBA żądań).
 *
 * ⚠️ Wyłączenie suwaka NAPRAWDĘ zdejmuje zachowanie: efekt zwraca funkcję zatrzymującą z `uruchomOchroneTresci`,
 * która odpina dokładnie te nasłuchy, które założyła, i zdejmuje atrybut z <body>. Nic nie zostaje przyklejone
 * do strony do następnego F5.
 *
 * ⚠️ Blokady nie dotyczą kont z rangą administratora — decyduje o tym SERWER (`blokadyStosowane`), żeby
 * administrator, który włączy blokadę F12, nie został bez narzędzi do diagnozy. Znak wodny obowiązuje wszystkich.
 */
const OchronaTresci = () => {
    const { user } = useAuth() || {};
    const idKonta = user?.id ?? null;
    const [ustawienia, setUstawienia] = useState(null);

    useEffect(() => {
        if (!idKonta) {
            setUstawienia(null);
            return undefined;
        }
        let zywy = true;
        let ostatnie = null;

        const pobierz = async (wymus) => {
            if (!zywy) return;
            if (!wymus && ostatnie !== null && Date.now() - ostatnie < ODSTEP_SPRAWDZEN_MS) return;
            ostatnie = Date.now();
            try {
                const odp = await apiService.getOchronaTresci();
                if (!zywy) return;
                const dane = odp?.data;
                if (!dane || typeof dane !== "object") return;
                // Tożsamość stanu zostaje, gdy nic się nie zmieniło — inaczej każde sprawdzenie
                // przekładałoby nasłuchy i przemontowywało znak wodny bez powodu.
                setUstawienia((stare) => (podpisUstawien(stare) === podpisUstawien(dane) ? stare : dane));
            } catch {
                /* błąd sieci: zostaje stan, jaki jest — ochrona treści nie może zablokować pracy aplikacji */
            }
        };

        const naPowrot = () => {
            if (typeof document !== "undefined" && document.visibilityState === "hidden") return;
            pobierz(false);
        };
        const naZmiane = () => pobierz(true);

        pobierz(true);
        document.addEventListener("visibilitychange", naPowrot);
        window.addEventListener("focus", naPowrot);
        window.addEventListener(ZDARZENIE_OCHRONY_TRESCI, naZmiane);

        return () => {
            zywy = false;
            document.removeEventListener("visibilitychange", naPowrot);
            window.removeEventListener("focus", naPowrot);
            window.removeEventListener(ZDARZENIE_OCHRONY_TRESCI, naZmiane);
        };
    }, [idKonta]);

    useEffect(() => {
        if (!ustawienia || !czyStosowacBlokady(ustawienia)) return undefined;
        return uruchomOchroneTresci(ustawienia);
    }, [ustawienia]);

    if (!ustawienia?.znakWodny) return null;
    return <ZnakWodny podpis={ustawienia.podpis} />;
};

export default OchronaTresci;
